<template>
  <div>
    <BaseSpinner
      v-if="(!services.length && fetchingServices) || fetchingServicePackages"
      inline
    />
    <div v-else-if="services.length">
      <BaseCard :heading="filters.capitalize($t('global.items.service', 2))">
        <Draggable
          :list="rows"
          item-key="key"
          :class="$style.partsWrap"
          handle=".js-drag-handle"
          @change="onChange"
        >
          <template #item="{ element, index }">
            <div>
              <Part
                :key="element.key"
                :part="formData.partsAttributes[element.partIndexes[0]]"
                :partIndexes="element.partIndexes"
                :servicePackage="element.servicePackage"
                :isExpanded="expandedRowIndex === index"
                :removable="element.kind === 'single' && formData.partsAttributes.length > 1 || formData.partsAttributes.length > element.partIndexes.length"
                @expand="expandedRowIndex = index"
                @delete="deleteRow(element.partIndexes)"
                v-test="'appointment-part'"
              />
            </div>
          </template>
        </Draggable>

        <BaseAlert
          v-if="existingAppointmentData.treatwell"
          color="warning"
          :text="$t('appointment.edit_warning')"
          v-test="'appointment-edit-warning'"
        />
        <BaseText
          v-else
          inline
          @click="onAddClick"
          v-test="'appointment-add-part'"
        >
          + {{ $t('appointment.create.add_service') }}
        </BaseText>
      </BaseCard>
    </div>
    <div
      v-else
      :class="$style.noServices"
    >
      <BaseText>{{ $t('appointment.no_services_found.text') }}</BaseText>
      <BaseText
        :routerLink="{
          name: 'admin-services'
        }"
        v-test="'no-services-link'"
      >
        {{ $t('appointment.no_services_found.link') }}
      </BaseText>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { defineComponent, ref } from 'vue';
import Draggable from 'vuedraggable';
import filters from '@/filters';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useServicesStore } from '@/stores/services';
import Part from './part/index.vue';
import { usePartRows } from './part-rows';

export default defineComponent({
  name: 'AppointmentParts',
  components: {
    Part,
    Draggable,
  },
  setup() {
    const { formData, addPart, resetParts, existingAppointmentData } =
      useCreateAppointmentStore();
    const { rows } = usePartRows();

    return {
      formData,
      addPart,
      resetParts,
      existingAppointmentData,
      rows,
      expandedRowIndex: ref(existingAppointmentData.id ? null : 0),
      filters,
    };
  },
  computed: {
    ...mapState(useServicesStore, [
      'services',
      'fetchingServices',
      'fetchingServicePackages',
    ]),
  },
  methods: {
    onChange({ moved }) {
      // This method will trigger when the order of the rows changes (a row is either
      // a single part, or a whole package group dragged as one unit).
      // Because we are using the index to show the expanded row, we need to update
      // expandedRowIndex so the correct row stays expanded.
      // The moved object contains the new and old index of the row that was moved.

      if (moved.oldIndex === this.expandedRowIndex) {
        this.expandedRowIndex = moved.newIndex;
      } else if (
        moved.oldIndex < this.expandedRowIndex &&
        moved.newIndex >= this.expandedRowIndex
      ) {
        this.expandedRowIndex--;
      } else if (
        moved.oldIndex > this.expandedRowIndex &&
        moved.newIndex <= this.expandedRowIndex
      ) {
        this.expandedRowIndex++;
      }

      // vuedraggable has already reordered `this.rows` in place (it's the same array
      // instance passed as :list). Flatten that new row order back into a flat
      // partsAttributes array, so a package group's parts move as one contiguous block.
      const flattened = this.rows.reduce(
        (parts, row) =>
          parts.concat(
            row.partIndexes.map(
              (partIndex) => this.formData.partsAttributes[partIndex],
            ),
          ),
        [],
      );

      // Update the sortOrder prop. This is not being used in the frontend, but it is needed when sending the parts to the backend
      // It's basically only needed for updating opponents, because sending a re-sorted array to the backend won't cause the parts to be re-sorted in the backend
      flattened.forEach((part, index) => {
        part.sortOrder = index;
      });

      this.formData.partsAttributes = flattened;
    },
    deleteRow(partIndexes) {
      if (this.formData.partsAttributes.length === partIndexes.length) {
        this.resetParts();
        return;
      }

      const rowIndex = this.rows.findIndex(
        (row) => row.partIndexes[0] === partIndexes[0],
      );

      [...partIndexes]
        .sort((a, b) => b - a)
        .forEach((index) => {
          this.formData.partsAttributes.splice(index, 1);
        });

      if (rowIndex === this.expandedRowIndex) {
        this.expandLastPart();
      }
    },
    onAddClick() {
      this.addPart();
      this.expandLastPart();
    },
    expandLastPart() {
      // Expand the newly added part by setting expandedRowIndex to the index of the last row.
      this.expandedRowIndex = this.rows.length - 1;
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.partsWrap {
  & > * {
    margin-bottom: $spacing * 0.5;
  }
}
</style>
